import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { X, Mail, Phone, MapPin, Calendar, CreditCard, AlertTriangle, CheckCircle, Clock, MessageSquare, Building2, RotateCw, Pencil, User, ShieldCheck, FileText } from 'lucide-react';
import { apiClient, Driver, DriverEmailHistoryResponse, UpdateDriverRequest, FleetSubtype } from '../../services/api';
import LicenceCheckModal from './LicenceCheckModal';
import EditDriverModal from './EditDriverModal';
import ConsentDetailsModal from './ConsentDetailsModal';

type Tab = 'driver' | 'licence' | 'email';

interface DriverViewModalProps {
    driver: Driver | null;
    isOpen: boolean;
    onClose: () => void;
    onDriverUpdated?: () => void;
    subtype?: FleetSubtype;
}

const DriverViewModal: React.FC<DriverViewModalProps> = ({ driver, isOpen, onClose, onDriverUpdated, subtype }) => {
    const [activeTab, setActiveTab] = useState<Tab>('driver');
    const [emailHistory, setEmailHistory] = useState<DriverEmailHistoryResponse | null>(null);
    const [isLoadingEmails, setIsLoadingEmails] = useState(false);
    const [emailError, setEmailError] = useState<string | null>(null);
    const [isReactivating, setIsReactivating] = useState(false);

    // Modal states
    const [showLicenceCheckModal, setShowLicenceCheckModal] = useState(false);
    const [showEditDriverModal, setShowEditDriverModal] = useState(false);
    const [showConsentDetailsModal, setShowConsentDetailsModal] = useState(false);
    const [isUpdatingDriver, setIsUpdatingDriver] = useState(false);

    // Reset state when modal closes
    const handleClose = () => {
        setActiveTab('driver');
        setEmailHistory(null);
        setEmailError(null);
        setShowLicenceCheckModal(false);
        setShowEditDriverModal(false);
        setShowConsentDetailsModal(false);
        onClose();
    };

    if (!isOpen || !driver) return null;

    const getRiskBadgeClass = (risk?: string) => {
        switch (risk) {
            case 'high':   return 'bg-red-100 text-red-800 border-red-200';
            case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'low':    return 'bg-green-100 text-green-800 border-green-200';
            default:       return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'Not provided';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    const calculateAge = (dateOfBirth: string) => {
        const today = new Date();
        const birthDate = new Date(dateOfBirth);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    const loadEmailHistory = async () => {
        if (!driver) return;
        setIsLoadingEmails(true);
        setEmailError(null);
        try {
            const response = await apiClient.getDriverEmailHistory(driver.id);
            setEmailHistory(response);
        } catch (error: any) {
            setEmailError(error.message || 'Failed to load email history');
        } finally {
            setIsLoadingEmails(false);
        }
    };

    const handleTabChange = (tab: Tab) => {
        setActiveTab(tab);
        if (tab === 'email' && !emailHistory && !isLoadingEmails) {
            loadEmailHistory();
        }
    };

    const handleReactivateDriver = async () => {
        if (!driver) return;
        if (window.confirm(`Are you sure you want to reactivate ${driver.firstName} ${driver.lastName}? They will need to provide consent again.`)) {
            try {
                setIsReactivating(true);
                const response = await apiClient.reactivateDriver(driver.id);
                toast.success(response.message || 'Driver reactivated successfully');
                if (onDriverUpdated) onDriverUpdated();
                handleClose();
            } catch (error: any) {
                console.error('Failed to reactivate driver:', error);
                toast.error('Failed to reactivate driver: ' + error.message);
            } finally {
                setIsReactivating(false);
            }
        }
    };

    const handleEditDriverSubmit = async (driverId: string, updatedDriver: UpdateDriverRequest) => {
        try {
            setIsUpdatingDriver(true);
            await apiClient.updateDriver(driverId, updatedDriver);
            toast.success('Driver updated successfully!');
            if (onDriverUpdated) onDriverUpdated();
            setShowEditDriverModal(false);
        } catch (error: any) {
            toast.error('Failed to update driver: ' + error.message);
            throw error;
        } finally {
            setIsUpdatingDriver(false);
        }
    };

    const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
        { id: 'driver',  label: 'Driver',             icon: <User className="w-4 h-4" /> },
        { id: 'licence', label: 'Licence & Compliance', icon: <ShieldCheck className="w-4 h-4" /> },
        { id: 'email',   label: 'Email History',       icon: <MessageSquare className="w-4 h-4" /> },
    ];

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">

                {/* Header — unchanged */}
                <div className="flex justify-between items-start p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-start space-x-3 sm:space-x-4 min-w-0 flex-1">
                        <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(105, 190, 40, 0.1)' }}>
                            <span className="text-lg sm:text-2xl font-bold" style={{ color: '#69BE28' }}>
                                {driver.firstName.charAt(0)}{driver.lastName.charAt(0)}
                            </span>
                        </div>
                        <div className="min-w-0 flex-1">
                            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                                {driver.firstName} {driver.lastName}
                            </h2>
                            <p className="text-sm sm:text-base text-gray-600 truncate">{driver.email}</p>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:space-x-2 mt-2">
                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border self-start ${getRiskBadgeClass(driver.riskLevel)}`}>
                                    {(driver.riskLevel || 'Unknown').charAt(0).toUpperCase() + (driver.riskLevel || 'Unknown').slice(1)} Risk
                                </span>
                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full self-start ${driver.active
                                    ? 'bg-green-100 text-green-800 border-green-200'
                                    : 'bg-red-100 text-red-800 border-red-200'
                                }`}>
                                    {driver.active ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-2 flex-shrink-0">
                        {driver.active && (
                            <button
                                onClick={() => setShowEditDriverModal(true)}
                                className="text-gray-600 hover:text-gray-800 transition-colors p-2 rounded-md hover:bg-gray-100"
                                title="Edit Driver"
                            >
                                <Pencil className="w-5 h-5" />
                            </button>
                        )}
                        <button
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                        >
                            <X className="w-5 h-5 sm:w-6 sm:h-6" />
                        </button>
                    </div>
                </div>

                {/* Tab bar */}
                <div className="flex border-b border-gray-200 flex-shrink-0 px-6">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => handleTabChange(tab.id)}
                            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
                                activeTab === tab.id
                                    ? 'border-green-500 text-green-700'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                            style={activeTab === tab.id ? { borderColor: '#69BE28', color: '#4a8a1c' } : {}}
                        >
                            {tab.icon}
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Tab content — scrollable */}
                <div className="flex-1 overflow-y-auto p-6">

                    {/* ── Tab 1: Driver ── */}
                    {activeTab === 'driver' && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                            {/* Personal Information */}
                            <div>
                                <h3 className="text-base font-semibold text-gray-900 mb-4">Personal Information</h3>
                                <div className="space-y-4">
                                    <div className="flex items-center space-x-3">
                                        <Calendar className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                        <div>
                                            <p className="text-sm text-gray-600">Date of Birth</p>
                                            <p className="text-sm font-medium text-gray-900">
                                                {formatDate(driver.dateOfBirth)} (Age: {calculateAge(driver.dateOfBirth)})
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center space-x-3">
                                        <Mail className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                        <div>
                                            <p className="text-sm text-gray-600">Email Address</p>
                                            <p className="text-sm font-medium text-gray-900">{driver.email}</p>
                                        </div>
                                    </div>

                                    {driver.phone && (
                                        <div className="flex items-center space-x-3">
                                            <Phone className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                            <div>
                                                <p className="text-sm text-gray-600">Phone Number</p>
                                                <p className="text-sm font-medium text-gray-900">{driver.phone}</p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex items-start space-x-3">
                                        <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm text-gray-600">Address</p>
                                            <div className="text-sm font-medium text-gray-900">
                                                {driver.addressLine1 && <p>{driver.addressLine1}</p>}
                                                {driver.addressLine2 && <p>{driver.addressLine2}</p>}
                                                {driver.city && driver.postcode && (
                                                    <p>{driver.city}, {driver.postcode}</p>
                                                )}
                                                {(!driver.addressLine1 && !driver.city && !driver.postcode) && (
                                                    <p className="text-gray-500 italic">No address provided</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-start space-x-3">
                                        <Building2 className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm text-gray-600">Site / Location</p>
                                            <div className="text-sm font-medium text-gray-900">
                                                {driver.site ? (
                                                    <>
                                                        <p>{driver.site.name} ({driver.site.code})</p>
                                                        {driver.site.city && driver.site.postcode && (
                                                            <p className="text-xs text-gray-500">{driver.site.city}, {driver.site.postcode}</p>
                                                        )}
                                                    </>
                                                ) : (
                                                    <p className="text-gray-500 italic">Unassigned</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Account Information + Inactive status */}
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-base font-semibold text-gray-900 mb-4">Account Information</h3>
                                    <div className="space-y-4">
                                        <div className="flex items-center space-x-3">
                                            <Calendar className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                            <div>
                                                <p className="text-sm text-gray-600">Account Created</p>
                                                <p className="text-sm font-medium text-gray-900">{formatDate(driver.createdAt)}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center space-x-3">
                                            <Clock className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                            <div>
                                                <p className="text-sm text-gray-600">Last Updated</p>
                                                <p className="text-sm font-medium text-gray-900">{formatDate(driver.updatedAt)}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Reactivate — only when inactive */}
                                {!driver.active && (
                                    <div>
                                        <h3 className="text-base font-semibold text-gray-900 mb-4">Driver Status</h3>
                                        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                                            <div className="flex items-start">
                                                <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
                                                <div className="flex-1">
                                                    <p className="text-sm font-medium text-yellow-800">Driver Inactive</p>
                                                    <p className="text-xs text-yellow-700 mt-1">
                                                        This driver has been deactivated. Reactivate them to enable operations. They will need to provide consent again.
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={handleReactivateDriver}
                                                disabled={isReactivating}
                                                className="w-full mt-3 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center justify-center"
                                            >
                                                {isReactivating ? (
                                                    <>
                                                        <div className="animate-spin h-4 w-4 border-2 border-t-transparent rounded-full mr-2"></div>
                                                        Reactivating...
                                                    </>
                                                ) : (
                                                    <>
                                                        <RotateCw className="w-4 h-4 mr-2" />
                                                        Reactivate Driver
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ── Tab 2: Licence & Compliance ── */}
                    {activeTab === 'licence' && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                                {/* Licence Information */}
                                <div>
                                    <h3 className="text-base font-semibold text-gray-900 mb-4">Licence Information</h3>
                                    <div className="space-y-4">
                                        <div className="flex items-center space-x-3">
                                            <CreditCard className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                            <div>
                                                <p className="text-sm text-gray-600">Licence Number</p>
                                                <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                                                    {driver.licenceNumber}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-center space-x-3">
                                            <AlertTriangle className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                            <div>
                                                <p className="text-sm text-gray-600">Risk Level</p>
                                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getRiskBadgeClass(driver.riskLevel)}`}>
                                                    {(driver.riskLevel || 'Not Assessed').charAt(0).toUpperCase() + (driver.riskLevel || 'Not Assessed').slice(1)}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="flex items-center space-x-3">
                                            <Clock className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                            <div>
                                                <p className="text-sm text-gray-600">Last Licence Check</p>
                                                <p className="text-sm font-medium text-gray-900">
                                                    {driver.lastLicenceCheck ? formatDate(driver.lastLicenceCheck) : 'Never checked'}
                                                </p>
                                            </div>
                                        </div>

                                    </div>
                                </div>

                                {/* Compliance Status */}
                                <div>
                                    <h3 className="text-base font-semibold text-gray-900 mb-4">Compliance Status</h3>
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                            <div className="flex items-center space-x-3">
                                                {driver.consentProvided ? (
                                                    <CheckCircle className="w-5 h-5 text-green-500" />
                                                ) : (
                                                    <AlertTriangle className="w-5 h-5 text-yellow-500" />
                                                )}
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900">D906 Consent</p>
                                                    <p className="text-xs text-gray-600">
                                                        {driver.consentProvided ? 'Provided' : 'Not provided'}
                                                    </p>
                                                </div>
                                            </div>
                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                                driver.consentProvided
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-yellow-100 text-yellow-800'
                                            }`}>
                                                {driver.consentProvided ? 'Valid' : 'Required'}
                                            </span>
                                        </div>

                                        {driver.consentExpiry && (
                                            <div className="flex items-center space-x-3">
                                                <Calendar className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                                <div>
                                                    <p className="text-sm text-gray-600">Consent Expiry</p>
                                                    <p className="text-sm font-medium text-gray-900">{formatDate(driver.consentExpiry)}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Unified action row — only for active drivers */}
                            {driver.active && (
                                <div className="flex gap-3 pt-4 border-t border-gray-100">
                                    <button
                                        onClick={() => setShowLicenceCheckModal(true)}
                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity"
                                        style={{ backgroundColor: '#69BE28' }}
                                    >
                                        <CreditCard className="w-4 h-4" />
                                        Licence Details
                                    </button>
                                    <button
                                        onClick={() => setShowConsentDetailsModal(true)}
                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        <FileText className="w-4 h-4" />
                                        Consent Details
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Tab 3: Email History ── */}
                    {activeTab === 'email' && (
                        <div>
                            {isLoadingEmails && (
                                <div className="text-center py-12">
                                    <div className="animate-spin h-8 w-8 border-2 border-t-transparent rounded-full mx-auto" style={{ borderColor: '#69BE28 transparent transparent transparent' }}></div>
                                    <p className="mt-3 text-sm text-gray-600">Loading email history...</p>
                                </div>
                            )}

                            {emailError && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                    <p className="text-sm text-red-800">{emailError}</p>
                                    <button
                                        onClick={loadEmailHistory}
                                        className="mt-2 px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                                    >
                                        Retry
                                    </button>
                                </div>
                            )}

                            {emailHistory && !isLoadingEmails && (
                                <div className="space-y-6">
                                    {/* Summary stats */}
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total</p>
                                            <p className="text-2xl font-bold text-gray-900">{emailHistory.data.summary.totalEmails}</p>
                                        </div>
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                                            <p className="text-xs text-green-600 uppercase tracking-wide mb-1">Delivered</p>
                                            <p className="text-2xl font-bold text-green-700">{emailHistory.data.summary.successfulEmails}</p>
                                        </div>
                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                                            <p className="text-xs text-yellow-600 uppercase tracking-wide mb-1">Pending</p>
                                            <p className="text-2xl font-bold text-yellow-700">{emailHistory.data.summary.pendingEmails}</p>
                                        </div>
                                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                                            <p className="text-xs text-red-500 uppercase tracking-wide mb-1">Failed</p>
                                            <p className="text-2xl font-bold text-red-600">{emailHistory.data.summary.failedEmails}</p>
                                        </div>
                                    </div>

                                    {/* Email log */}
                                    {emailHistory.data.emailHistory && emailHistory.data.emailHistory.length > 0 ? (
                                        <div>
                                            <h4 className="text-sm font-semibold text-gray-700 mb-3">Recent Emails</h4>
                                            <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                                                {emailHistory.data.emailHistory.slice(0, 5).map((log) => (
                                                    <div key={log.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-900 capitalize">
                                                                {log.emailType.replace(/_/g, ' ')}
                                                            </p>
                                                            <p className="text-xs text-gray-500 mt-0.5">
                                                                {new Date(log.sentAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                                {log.retryCount > 0 && (
                                                                    <span className="ml-2 text-orange-500">{log.retryCount} {log.retryCount === 1 ? 'retry' : 'retries'}</span>
                                                                )}
                                                            </p>
                                                        </div>
                                                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                                                            log.deliveryStatus === 'delivered' ? 'bg-green-100 text-green-800' :
                                                            log.deliveryStatus === 'bounced'   ? 'bg-red-100 text-red-800' :
                                                                                                 'bg-yellow-100 text-yellow-800'
                                                        }`}>
                                                            {log.deliveryStatus}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center py-12 text-gray-500">
                                            <MessageSquare className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                                            <p className="text-sm">No emails sent to this driver yet.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end flex-shrink-0">
                    <button
                        onClick={handleClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2"
                        style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                    >
                        Close
                    </button>
                </div>
            </div>

            {/* Licence Check Modal */}
            <LicenceCheckModal
                isOpen={showLicenceCheckModal}
                onClose={() => setShowLicenceCheckModal(false)}
                driver={driver}
                onCheckComplete={() => {
                    setShowLicenceCheckModal(false);
                    if (onDriverUpdated) onDriverUpdated();
                }}
                subtype={subtype}
            />

            {/* Edit Driver Modal */}
            <EditDriverModal
                isOpen={showEditDriverModal}
                onClose={() => setShowEditDriverModal(false)}
                onSubmit={handleEditDriverSubmit}
                isUpdating={isUpdatingDriver}
                driver={driver}
            />

            {/* Consent Details Modal */}
            {showConsentDetailsModal && (
                <ConsentDetailsModal
                    driver={driver}
                    onClose={() => setShowConsentDetailsModal(false)}
                    onRefresh={() => {
                        if (onDriverUpdated) onDriverUpdated();
                    }}
                />
            )}
        </div>
    );
};

export default DriverViewModal;
